import { useEffect, useState } from 'react';
import { Globe, Loader2, ExternalLink, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { Card, CardContent } from '../ui/Card';
import Button from '../ui/Button';
import cultivarService from '../../services/cultivar';
import type { Cultivar, CultivarMedia } from '../../types/cultivar';

interface WebsitePublishPanelProps {
  cultivar: Cultivar;
  onUpdated?: (cultivar: Cultivar) => void;
}

/**
 * Inline panel on the cultivar edit page. Toggles `show_on_website` on the
 * cultivar (which exposes it via /api/public/...) and lets the owner edit the
 * editorial copy and pick a hero image from the cultivar's public media.
 */
function WebsitePublishPanel({ cultivar, onUpdated }: WebsitePublishPanelProps) {
  const [showOnWebsite, setShowOnWebsite] = useState(!!cultivar.show_on_website);
  const [tagline, setTagline] = useState(cultivar.website_tagline ?? '');
  const [heroMediaId, setHeroMediaId] = useState<number | null>(cultivar.website_hero_media_id ?? null);
  const [sortOrder, setSortOrder] = useState<number>(cultivar.website_sort_order ?? 0);

  const [media, setMedia] = useState<CultivarMedia[]>([]);
  const [mediaLoading, setMediaLoading] = useState(true);
  const [mediaUrls, setMediaUrls] = useState<Record<number, string>>({});

  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const websiteOrigin = import.meta.env.VITE_PUBLIC_WEBSITE_URL || 'https://website.factree.com.au';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setMediaLoading(true);
      try {
        const res = await cultivarService.listCultivarMedia(cultivar.id);
        if (!cancelled && res.success && res.data) {
          setMedia(res.data.filter((m) => m.visibility === 'public' && m.kind === 'photo'));
        }
      } finally {
        if (!cancelled) setMediaLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [cultivar.id]);

  // <img src> can't send the Authorization header, so fetch each file via the
  // authenticated helper and hand the resulting blob URL to <img>.
  useEffect(() => {
    let cancelled = false;
    const created: string[] = [];
    (async () => {
      const next: Record<number, string> = {};
      await Promise.all(
        media.map(async (m) => {
          try {
            const url = await cultivarService.fetchCultivarMediaBlobUrl(m.id);
            if (cancelled) {
              URL.revokeObjectURL(url);
              return;
            }
            next[m.id] = url;
            created.push(url);
          } catch {
            // Skip — tile will render without an image; the rest still load.
          }
        })
      );
      if (!cancelled) setMediaUrls(next);
    })();
    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [media]);

  const handleTogglePublish = async (next: boolean) => {
    setError(null);
    setSaving(true);
    try {
      const res = await cultivarService.setWebsitePublish(cultivar.id, next);
      if (res.success && res.data) {
        setShowOnWebsite(next);
        setSavedAt(new Date().toISOString());
        onUpdated?.(res.data);
      } else {
        setError(res.message || 'Failed to update publish state');
      }
    } catch (e) {
      console.error(e);
      setError('Failed to update publish state');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCopy = async () => {
    setError(null);
    setSaving(true);
    try {
      const res = await cultivarService.updateCultivar(cultivar.id, {
        website_tagline: tagline || null,
        website_hero_media_id: heroMediaId,
        website_sort_order: sortOrder,
      });
      if (res.success && res.data) {
        setSavedAt(new Date().toISOString());
        onUpdated?.(res.data);
      } else {
        setError(res.message || 'Failed to save website details');
      }
    } catch (e) {
      console.error(e);
      setError('Failed to save website details');
    } finally {
      setSaving(false);
    }
  };

  const publicUrl = `${websiteOrigin.replace(/\/$/, '')}/cultivar/${cultivar.id}`;

  return (
    <Card className="mt-6">
      <CardContent className="p-6 sm:p-8">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <Globe className="h-5 w-5 text-emerald-700" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">Public Website</h3>
              <p className="text-sm text-neutral-600">
                Show this cultivar on{' '}
                <a href={websiteOrigin} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">
                  {websiteOrigin.replace(/^https?:\/\//, '')}
                </a>{' '}
                so commercial growers can browse it without an account.
              </p>
            </div>
          </div>

          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showOnWebsite}
              onChange={(e) => handleTogglePublish(e.target.checked)}
              disabled={saving}
              className="h-5 w-5 rounded border-neutral-300 text-emerald-600 focus:ring-emerald-500"
            />
            <span className={`text-sm font-medium ${showOnWebsite ? 'text-emerald-700' : 'text-neutral-500'}`}>
              {showOnWebsite ? 'Published' : 'Not published'}
            </span>
          </label>
        </div>

        {error && (
          <div className="mb-4 flex items-start gap-2 rounded-md bg-error-50 border border-error-200 p-3 text-sm text-error-700">
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Tagline <span className="text-neutral-400 font-normal">(short headline shown on cards)</span>
            </label>
            <input
              type="text"
              maxLength={200}
              value={tagline}
              onChange={(e) => setTagline(e.target.value)}
              placeholder="e.g. Late-season firm-fleshed plum with strong shelf life"
              className="w-full rounded-md border-neutral-300 focus:border-emerald-500 focus:ring-emerald-500 text-sm"
            />
            <p className="mt-1 text-xs text-neutral-500">{tagline.length}/200</p>
          </div>

          <div className="rounded-md border border-neutral-200 bg-neutral-50 p-3 text-xs text-neutral-600">
            The cultivar's main <strong>Description</strong> (edited in the form above) is used as the
            body copy on the public website. Update it there — there's no separate website description.
          </div>

          <div>
            <label className="block text-sm font-medium text-neutral-700 mb-1">
              Hero image <span className="text-neutral-400 font-normal">(pick from photos marked visibility: public)</span>
            </label>
            {mediaLoading ? (
              <div className="flex items-center gap-2 text-sm text-neutral-500 py-3">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading photos...
              </div>
            ) : media.length === 0 ? (
              <div className="rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-4 text-sm text-neutral-600 flex items-start gap-2">
                <ImageIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>
                  No public photos uploaded yet. Upload a photo and set its visibility to <strong>public</strong> in the
                  media section to use it as the website hero.
                </span>
              </div>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
                {media.map((m) => {
                  const selected = heroMediaId === m.id;
                  return (
                    <button
                      type="button"
                      key={m.id}
                      onClick={() => setHeroMediaId(selected ? null : m.id)}
                      className={`relative aspect-square rounded-md overflow-hidden border-2 transition-colors ${
                        selected ? 'border-emerald-500 ring-2 ring-emerald-200' : 'border-neutral-200 hover:border-neutral-400'
                      }`}
                      title={m.caption || m.file_name}
                    >
                      {mediaUrls[m.id] ? (
                        <img
                          src={mediaUrls[m.id]}
                          alt={m.caption || m.file_name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-neutral-100" />
                      )}
                      {selected && (
                        <span className="absolute bottom-1 right-1 bg-emerald-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded">
                          Hero
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Sort order</label>
              <input
                type="number"
                value={sortOrder}
                onChange={(e) => setSortOrder(Number(e.target.value) || 0)}
                className="w-28 rounded-md border-neutral-300 focus:border-emerald-500 focus:ring-emerald-500 text-sm"
              />
              <p className="mt-1 text-xs text-neutral-500">Lower numbers appear first on the species page.</p>
            </div>
          </div>
        </div>

        <div className="mt-5 flex items-center justify-between border-t border-neutral-100 pt-4">
          <div className="text-xs text-neutral-500">
            {showOnWebsite ? (
              <a href={publicUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-emerald-700 hover:underline">
                View on website <ExternalLink className="h-3 w-3" />
              </a>
            ) : (
              <span>Toggle "Published" above to make this cultivar visible on the public site.</span>
            )}
            {savedAt && <span className="ml-3 text-neutral-400">Saved</span>}
          </div>
          <Button variant="primary" onClick={handleSaveCopy} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save website details'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default WebsitePublishPanel;
